/**
 * components/client/QuestionCard.tsx
 *
 * Displays a single question in the client form experience.
 * Handles animated entry (horizontal slide), question label, field renderer,
 * navigation buttons, error display.
 *
 * Updated June 2026 (refactor):
 * - Removed "Remplir plus tard" / skipped fields feature.
 * - Required fields now block the Next button (disabled) until filled.
 * - Optional empty fields show a "Passer" button (secondary, no chevron).
 * - Last step shows standard "Terminer" button, disabled when canProceed is false.
 * - Removed skippedFields and onSkippedFieldsOpen props.
 */

import { ChevronLeft, ChevronRight, AlertCircle, Info } from 'lucide-react';
import Button from '../ui/Button';
import FieldRenderer from './FieldRenderer';
import type { RequestField } from '../../utils/previewHelpers';
import type { UploadedFileEntry } from '../../hooks/useClientForm';

interface QuestionCardProps {
  field: RequestField;
  response: string;
  uploadedFiles: UploadedFileEntry[];
  direction: 'forward' | 'backward';
  currentStep: number;
  isLastStep: boolean;
  canProceed: boolean;
  submitting: boolean;
  error: string;
  onResponseChange: (value: string) => void;
  onFilesSelect: (files: File[]) => void;
  onFileRemove: (index: number) => void;
  onFileRetry: (index: number) => void;
  onPrevious: () => void;
  onNext: () => void;
  onSubmit: () => void;
  onAutoAdvance: () => void;
}

export default function QuestionCard({
  field,
  response,
  uploadedFiles,
  direction,
  currentStep,
  isLastStep,
  canProceed,
  submitting,
  error,
  onResponseChange,
  onFilesSelect,
  onFileRemove,
  onFileRetry,
  onPrevious,
  onNext,
  onSubmit,
  onAutoAdvance,
}: QuestionCardProps) {
  const animClass = direction === 'forward' ? 'animate-slide-from-right' : 'animate-slide-from-left';
  const isUploading = uploadedFiles.some((f) => f.uploading);
  const isInfoField = field.type === 'info';

  const isFieldEmpty = field.type === 'document'
    ? (uploadedFiles.filter((f) => f.uploaded).length === 0)
    : !response.trim();

  // "Passer" only for optional empty fields; required empty → button disabled via canProceed
  const isSkipping = !isInfoField && !field.is_required && isFieldEmpty;

  const nextButtonLabel = (() => {
    if (isInfoField) return "J'ai compris";
    if (isSkipping) return 'Passer';
    return 'Suivant';
  })();

  const navFooter = (
    <div className="flex items-center justify-between gap-3 mt-6 pt-5 border-t border-neutral-100">
      <Button
        onClick={onPrevious}
        disabled={currentStep === 0}
        variant="secondary"
        className="flex-shrink-0"
      >
        <ChevronLeft className="w-4 h-4 mr-1" />
        <span className="hidden sm:inline">Précédent</span>
        <span className="sm:hidden">Retour</span>
      </Button>

      {isLastStep ? (
        <Button onClick={onSubmit} disabled={submitting || isUploading || !canProceed}>
          {submitting ? 'Finalisation...' : 'Terminer'}
        </Button>
      ) : (
        <Button
          onClick={onNext}
          disabled={!canProceed || isUploading}
          variant={isSkipping ? 'secondary' : 'primary'}
        >
          {nextButtonLabel}
          {!isSkipping && <ChevronRight className="w-4 h-4 ml-1" />}
        </Button>
      )}
    </div>
  );

  if (isInfoField) {
    return (
      <div key={field.id} className={`w-full max-w-xl ${animClass}`}>
        <div className="bg-white rounded-3xl shadow-soft-xl p-6 md:p-8">
          {error && (
            <div className="mb-5 bg-error-50 border border-error-200 text-error-700 px-4 py-3 rounded-xl flex items-start gap-2">
              <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          <div className="rounded-2xl bg-primary-50 border border-primary-200 p-5 md:p-6">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center">
                <Info className="w-5 h-5 text-primary-600" />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-lg md:text-xl font-bold text-primary-900 leading-snug">
                  {field.label}
                </h2>
                {field.description && (
                  <p className="mt-3 text-base text-primary-800 leading-relaxed">
                    {field.description}
                  </p>
                )}
              </div>
            </div>
          </div>

          {navFooter}
        </div>
      </div>
    );
  }

  return (
    <div key={field.id} className={`w-full max-w-xl ${animClass}`}>
      <div className="bg-white rounded-3xl shadow-soft-xl p-6 md:p-8">
        {error && (
          <div className="mb-5 bg-error-50 border border-error-200 text-error-700 px-4 py-3 rounded-xl flex items-start gap-2">
            <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <span className="text-sm">{error}</span>
          </div>
        )}

        <div className="mb-5">
          <h2 className="text-lg md:text-2xl font-bold text-neutral-900 leading-snug">
            {field.label}
            {field.is_required && <span className="text-error-500 ml-1">*</span>}
          </h2>
          {!field.is_required && (
            <span className="inline-block mt-2 text-xs font-medium text-neutral-400 bg-neutral-100 px-2.5 py-1 rounded-full">
              Optionnel
            </span>
          )}
          {field.description && (
            <p className="text-neutral-500 leading-relaxed mt-2 text-sm md:text-base">
              {field.description}
            </p>
          )}
        </div>

        <FieldRenderer
          field={field}
          response={response}
          uploadedFiles={uploadedFiles}
          onResponseChange={onResponseChange}
          onFilesSelect={onFilesSelect}
          onFileRemove={onFileRemove}
          onFileRetry={onFileRetry}
          onAutoAdvance={onAutoAdvance}
        />

        {navFooter}
      </div>
    </div>
  );
}
